import { FC, useCallback } from 'react';

import {
    CHANGE_HISTORY_TABLE_HEADERS_WIDTHS,
    ChangeHistoryTableHeaders,
    ChangeHistoryType,
} from 'utils/constants/admin';

import {
    Divider,
    Flex,
    Modal,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
    Text,
    Title,
    VSpacing,
} from '@hh.ru/magritte-ui';
import { CrossOutlinedSize24 } from '@hh.ru/magritte-ui/icon';

import useTrls from 'hooks/translation/useTrls';

import { ChangeHistoryEvent } from 'components/common/ChangeHistoryModal/components/ChangeHistoryEvent/ChangeHistoryEvent';

import { setLoadBannerAdminAction } from 'store/models/adminBannerAction';
import { setLoadCampaignAdminAction } from 'store/models/adminCampaignAction';
import { setShowModal } from 'store/models/changeHistory';
import { AppThunkAction, useDispatch, useSelector } from 'store/utils';

import styles from 'components/common/ChangeHistoryModal/ChangeHistoryModal.module.less';

const closeChangeHistoryModal = (): AppThunkAction<void> => (dispatch, getState) => {
    const { historyType } = getState().changeHistory;

    dispatch([
        setShowModal(false),
        historyType === ChangeHistoryType.Banner ? setLoadBannerAdminAction(false) : setLoadCampaignAdminAction(false),
    ]);
};

const TrlKeys = {
    title: 'campaign.info.sudo.actions.changeHistory.title',
    tableHeaders: {
        [ChangeHistoryTableHeaders.User]: 'campaign.info.sudo.actions.changeHistory.user',
        [ChangeHistoryTableHeaders.RealUser]: 'campaign.info.sudo.actions.changeHistory.realUser',
        [ChangeHistoryTableHeaders.UserIpAddress]: 'campaign.info.sudo.actions.changeHistory.userIpAddress',
        [ChangeHistoryTableHeaders.Date]: 'campaign.info.sudo.actions.changeHistory.date',
        [ChangeHistoryTableHeaders.Field]: 'campaign.info.sudo.actions.changeHistory.field',
        [ChangeHistoryTableHeaders.OldValue]: 'campaign.info.sudo.actions.changeHistory.oldValue',
        [ChangeHistoryTableHeaders.NewValue]: 'campaign.info.sudo.actions.changeHistory.newValue',
        [ChangeHistoryTableHeaders.ChangeSource]: 'campaign.info.sudo.actions.changeHistory.changeSource',
    },
    close: 'common.close',
};

const CHANGE_HISTORY_TABLE_HEADERS = [
    ChangeHistoryTableHeaders.User,
    ChangeHistoryTableHeaders.Date,
    ChangeHistoryTableHeaders.Field,
    ChangeHistoryTableHeaders.OldValue,
    ChangeHistoryTableHeaders.NewValue,
    ChangeHistoryTableHeaders.ChangeSource,
];

export const ChangeHistoryModal: FC = () => {
    const { isShow, historyType, events } = useSelector((state) => state.changeHistory);
    const dispatch = useDispatch();

    const trls = useTrls();

    const handleCloseClick = useCallback(() => {
        void dispatch(closeChangeHistoryModal());
    }, [dispatch]);

    const content = (
        <>
            <div className={styles.stickyHeader}>
                <Table
                    paddingLeft={16}
                    paddingRight={16}
                    cellsGap={16}
                    density={8}
                    topPanel={
                        <Flex justify="space-between" align="center" pt={16}>
                            <Title Element="h4" size="medium" style="primary">
                                {trls(TrlKeys.title)}
                            </Title>
                            <CrossOutlinedSize24
                                initialColor="secondary"
                                onClick={handleCloseClick}
                                data-qa="change-history-modal-close"
                                padding={8}
                            />
                        </Flex>
                    }
                >
                    <TableHeader>
                        <TableRow align="left">
                            {CHANGE_HISTORY_TABLE_HEADERS.map((header) => (
                                <TableCell
                                    Element="th"
                                    key={header}
                                    width={CHANGE_HISTORY_TABLE_HEADERS_WIDTHS[header]}
                                >
                                    <Text style="secondary" typography="label-4-regular">
                                        {trls(TrlKeys.tableHeaders[header])}
                                    </Text>
                                    {header === ChangeHistoryTableHeaders.User && (
                                        <>
                                            <VSpacing default={2} />
                                            <Text style="tertiary" typography="label-4-regular">
                                                {trls(TrlKeys.tableHeaders[ChangeHistoryTableHeaders.RealUser])}
                                            </Text>
                                        </>
                                    )}
                                    {header === ChangeHistoryTableHeaders.Date && (
                                        <>
                                            <VSpacing default={2} />
                                            <Text style="tertiary" typography="label-4-regular">
                                                {trls(TrlKeys.tableHeaders[ChangeHistoryTableHeaders.UserIpAddress])}
                                            </Text>
                                        </>
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHeader>
                </Table>
                <Divider />
            </div>
            <Table divider fixedSize stickyHeader paddingLeft={16} paddingRight={16} cellsGap={16} density={8}>
                <TableBody>
                    {events.map((event) => (
                        <ChangeHistoryEvent
                            key={`${event.changeTime}_${event.fieldName as string}_${event.newValue || ''}`}
                            event={event}
                            historyType={historyType}
                        />
                    ))}
                </TableBody>
            </Table>
        </>
    );

    return (
        <Modal
            visible={isShow}
            onClose={handleCloseClick}
            size="large"
            disableVerticalPaddings
            disableHorizontalPaddings
        >
            <div className={styles.contentWrapper}>{content}</div>
        </Modal>
    );
};
